import { type Accessor, createMemo } from 'solid-js';
import { type ITagItem } from '@tcweb/connect-ui';
import { translate } from '@fulcrum/lib';
import type { ProjectQueries } from './useProjectQueries';
import type {
  IssueResponse,
  GetConfigurationResponse,
} from '@data-access/connect/tc-issue-management';
import { findProjectUser, sortLogic } from '../utils/util';
import {
  useApiServices,
  type BatchTagAction,
  type TagResponse,
  type BatchTagResponse,
} from '@trimble/tc-issue-management-providers';
import type { CheckFollowStatusBulkResponse } from '@data-access/connect/tc-follow';
import type { GetAccessPolicyResponse } from '@data-access/connect/tc-ecom';

// Improved type definitions
interface ProjectUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'ACTIVE' | 'REMOVED' | string;
  tiduuid?: string;
}

interface ProjectGroup {
  id: string;
  name: string;
}

// Extended IssueResponse with tags
export interface IssueResponseWithTags extends IssueResponse {
  tags?: TagResponse[];
}

// Return type for useTransformedData
export interface TransformedData {
  issuesData: Accessor<{
    items: IssueResponseWithTags[];
    totalCount: number;
  } | null>;
  projectTagsData: Accessor<ITagItem[]>;
  projectUsersData: Accessor<readonly ProjectUser[] | null>;
  projectGroupsData: Accessor<readonly ProjectGroup[] | null>;
  configurationData: Accessor<GetConfigurationResponse | null>;
  accessPolicyData: Accessor<GetAccessPolicyResponse | null>;
  allAssigneesData: Accessor<ITagItem[]>;
  followStatusData: Accessor<CheckFollowStatusBulkResponse | null>;
}

/**
 * Custom hook for data transformation logic
 */
export function useTransformedData(
  queries: ProjectQueries,
  sortOption?: string,
  filterOption?: string,
  sortType?: 'ASC' | 'DESC',
  selectedTags?: ITagItem[]
): TransformedData {
  const sortIssues = (
    issues: IssueResponseWithTags[],
    sortBy: string,
    sortType: 'ASC' | 'DESC'
  ): IssueResponseWithTags[] => {
    const sortedIssues = [...issues];

    if (sortBy === translate('TC.Common.AssignedTo')) {
      return sortedIssues.sort((a, b) => {
        const aAssignee = a.assignees?.[0] || '';
        const bAssignee = b.assignees?.[0] || '';
        return sortLogic(aAssignee, bAssignee, sortType);
      });
    } else if (sortBy === translate('TC.Common.Author')) {
      return sortedIssues.sort((a, b) => {
        return sortLogic(a.createdBy, b.createdBy, sortType);
      });
    } else if (sortBy === translate('TC.Common.CreationDate')) {
      return sortedIssues.sort((a, b) => {
        return sortLogic(
          new Date(a.createdAt).getTime().toString(),
          new Date(b.createdAt).getTime().toString(),
          sortType
        );
      });
    } else if (sortBy === translate('TC.Common.DateModified')) {
      return sortedIssues.sort((a, b) => {
        return sortLogic(
          new Date(a.updatedAt).getTime().toString(),
          new Date(b.updatedAt).getTime().toString(),
          sortType
        );
      });
    } else if (sortBy === translate('TC.Common.Priority')) {
      return sortedIssues.sort((a, b) => {
        const priorityOrder: { [key: string]: number } = {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- Critical is an API response value
          Critical: 4,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- High is an API response value
          High: 3,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- Normal is an API response value
          Normal: 2,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- Low is an API response value
          Low: 1,
        };
        const aPriority = a.priority ? priorityOrder[a.priority] || 0 : 0;
        const bPriority = b.priority ? priorityOrder[b.priority] || 0 : 0;
        return sortLogic(bPriority.toString(), aPriority.toString(), sortType);
      });
    } else if (sortBy === translate('TC.Common.Status')) {
      return sortedIssues.sort((a, b): number => {
        const statusOrder: { [key: string]: number } = {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- New is an API response value
          New: 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- 'In Progress' is an API response value
          'In Progress': 2,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- Waiting is an API response value
          Waiting: 3,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- Done is an API response value
          Done: 4,
        };
        const aStatus = a.status ? statusOrder[a.status] || 0 : 0;
        const bStatus = b.status ? statusOrder[b.status] || 0 : 0;
        return sortLogic(aStatus.toString(), bStatus.toString(), sortType);
      });
    } else if (sortBy === translate('TC.Common.Title')) {
      return sortedIssues.sort((a, b) => {
        return sortLogic(a.title, b.title, sortType);
      });
    } else if (sortBy === translate('TC.Common.Type')) {
      return sortedIssues.sort((a, b) => {
        const aType = a.type || '';
        const bType = b.type || '';
        return sortLogic(aType, bType, sortType);
      });
    } else if (sortBy === translate('TC.Common.Tags') || sortBy === 'Tags') {
      return sortedIssues.sort((a, b) => {
        // Get the first tag label for sorting, or empty string if no tags
        const aTagLabel = a.tags?.[0]?.label || '';
        const bTagLabel = b.tags?.[0]?.label || '';
        return sortLogic(aTagLabel, bTagLabel, sortType);
      });
    } else {
      return sortedIssues;
    }
  };

  const searchIssues = (
    issues: IssueResponseWithTags[],
    selectedTags: ITagItem[],
    projectUsers?: Array<ProjectUser | null | undefined>
  ): IssueResponseWithTags[] => {
    if (!selectedTags || selectedTags.length === 0) {
      return issues;
    }
    return issues.filter((issue) => {
      return selectedTags.every((tag) => {
        const [field, query] = tag.value.split(':');
        const queryString =
          typeof query === 'string' ? query : String(query || '');
        const searchQuery = queryString.toLowerCase().trim();

        if (!searchQuery) return true;

        switch (field.toLowerCase()) {
          case 'all':
            return (
              issue.title?.toLowerCase().includes(searchQuery) ||
              issue.description?.toLowerCase().includes(searchQuery) ||
              issue.type?.toLowerCase().includes(searchQuery) ||
              issue.status?.toLowerCase().includes(searchQuery) ||
              issue.priority?.toLowerCase().includes(searchQuery) ||
              issue.tags?.some((issueTag) =>
                issueTag.label?.toLowerCase().includes(searchQuery)
              )
            );

          case 'title':
            return issue.title?.toLowerCase().includes(searchQuery) || false;

          case 'creation_author_uuid': {
            if (!projectUsers) return false;
            const createdByUuid = issue.createdBy?.includes(':')
              ? issue.createdBy.split(':')[1]
              : issue.createdBy;
            const user = findProjectUser(projectUsers, createdByUuid);
            if (!user) return false;
            const userName = `${user.firstName} ${user.lastName}`.toLowerCase();
            return userName.includes(searchQuery);
          }

          case 'description':
            return (
              issue.description?.toLowerCase().includes(searchQuery) || false
            );

          case 'type':
            return issue.type?.toLowerCase().includes(searchQuery) || false;

          case 'status':
            return issue.status?.toLowerCase().includes(searchQuery) || false;

          case 'priority':
            return issue.priority?.toLowerCase().includes(searchQuery) || false;

          case 'assignees':
            return (
              issue.assignees?.some((assignee) =>
                assignee?.toLowerCase().includes(searchQuery)
              ) || false
            );

          case 'tags':
            return (
              issue.tags?.some((issueTag) =>
                issueTag.label?.toLowerCase().includes(searchQuery)
              ) || false
            );

          default:
            return false;
        }
      });
    });
  };

  const filterIssues = (
    _queries: ProjectQueries,
    issues: IssueResponseWithTags[],
    filterBy: string,
    currentUserId?: string,
    followStatusData?: CheckFollowStatusBulkResponse
  ): IssueResponseWithTags[] => {
    switch (filterBy) {
      case translate('TC.Topic.AllProjectTopics'):
        return issues;
      case translate('TC.Topic.ActiveTopics3D'):
        return issues.filter(
          (issue) =>
            issue.status && !['Closed', 'Resolved'].includes(issue.status)
        );
      case translate('TC.Common.AssignedToMe'):
        if (!currentUserId) return [];
        return issues.filter(
          (issue) =>
            issue.assignees &&
            issue.assignees.some(
              (assignee) =>
                assignee === currentUserId ||
                assignee === `user:${currentUserId}` ||
                assignee === `users:${currentUserId}`
            )
        );

      case translate('TC.Common.CreatedByMe'):
        if (!currentUserId) return [];
        return issues.filter(
          (issue) =>
            issue.createdBy === currentUserId ||
            issue.createdBy === `user:${currentUserId}` ||
            issue.createdBy === `users:${currentUserId}`
        );

      case translate('TC.Topic.Followed'):
        if (followStatusData?.resources) {
          const followedIssues = followStatusData.resources!.filter(
            (resource) => resource.following === true
          );
          return issues.filter((issue) =>
            followedIssues.some(
              (resource) => resource.resourceId === `issues:${issue.id}`
            )
          );
        }
        return [];
      default:
        return issues;
    }
  };

  // Get API services for batch tags query
  const apiServices = useApiServices();

  // Create batch actions for all issues to fetch their tags
  const batchTagActions = createMemo((): BatchTagAction[] => {
    const query = queries.issuesQuery();
    if (!query?.data?.items) {
      return [];
    }

    const issues = query.data.items;
    const CONTEXT_ID_PROJECT_INDEX = 4;
    return issues.map((issue) => ({
      queryParams: {
        projectId: issue.contextId.split(':')[CONTEXT_ID_PROJECT_INDEX], // Extract project ID from contextId
        objectId: issue.id,
        objectType: 'ISSUE' as const,
      },
    }));
  });

  // Batch tags query - fetches tags for all issues
  // Wrap in createMemo to ensure reactivity when batchTagActions changes
  const batchTagActionsMemo = createMemo(() => {
    const actions = batchTagActions();
    return actions.length > 0 ? actions : undefined;
  });
  // eslint-disable-next-line solid/reactivity -- batchTagActionsMemo is a memoized value that needs to be accessed reactively
  const batchTagsQuery = apiServices.tagApi.getTagsBatchQuery(() =>
    batchTagActionsMemo()
  );

  // Helper function to merge tags into issues
  const mergeTagsIntoIssues = (
    issues: IssueResponse[],
    batchTagsResponse?: BatchTagResponse
  ): IssueResponseWithTags[] => {
    if (!batchTagsResponse) {
      return issues.map((issue) => ({ ...issue, tags: [] }));
    }

    const tagsMap = new Map<string, TagResponse[]>();
    batchTagsResponse.actions.forEach((action) => {
      const actionTyped = action as {
        queryParams: { objectId: string };
        responseBody?: TagResponse[];
      };
      const key = actionTyped.queryParams.objectId;
      tagsMap.set(key, actionTyped.responseBody || []);
    });

    return issues.map((issue) => ({
      ...issue,
      tags: tagsMap.get(issue.id) || [],
    }));
  };

  const issuesData = createMemo(() => {
    const query = queries.issuesQuery();
    if (!query?.data) {
      return null;
    }

    const issuesData = query.data;
    const currentSortOption = sortOption;
    const currentFilterOption = filterOption;
    const currentSortType = sortType;
    const currentSelectedTags = selectedTags;
    const currentUserId =
      queries.userQuery.data?.tiduuid || queries.userQuery.data?.id;
    const currentProjectUsers = queries.projectUsersQuery.data as
      | ProjectUser[]
      | undefined;

    // Get follow status data for filtering
    const followQuery = queries.followStatusQuery();
    const currentFollowStatusData = followQuery?.data;

    // Merge tags into issues first
    const issuesWithTags = mergeTagsIntoIssues(
      issuesData.items || [],
      batchTagsQuery.data
    );

    let processedItems = issuesWithTags;

    // Apply search filtering if search tags are provided
    if (
      currentSelectedTags &&
      currentSelectedTags.length > 0 &&
      processedItems.length > 0
    ) {
      processedItems = searchIssues(
        processedItems,
        currentSelectedTags,
        currentProjectUsers
      );
    }

    if (currentFilterOption && processedItems.length > 0) {
      processedItems = filterIssues(
        queries,
        processedItems,
        currentFilterOption,
        currentUserId,
        currentFollowStatusData
      );
    }

    if (currentSortOption && processedItems.length > 0) {
      processedItems = sortIssues(
        processedItems,
        currentSortOption,
        currentSortType as 'ASC' | 'DESC'
      );
    }
    return {
      ...issuesData,
      items: processedItems,
    };
  });

  const projectTagsData = createMemo((): ITagItem[] => {
    if (!queries.tagsQuery.data) {
      return [];
    }

    // Transform API response to ITagItem format
    return queries.tagsQuery.data.map(
      (tag): ITagItem => ({
        label: tag.label,
        value: tag.label, // Use label as value since tags are typically identified by label
        isValid: true,
        id: tag.id,
        color: tag.color,
      })
    );
  });

  // Get project users data
  const projectUsersData = createMemo(() => {
    return queries.projectUsersQuery.data ?? null;
  });

  // Get project groups data
  const projectGroupsData = createMemo(() => {
    return queries.projectGroupsQuery.data ?? null;
  });

  // Get configuration data
  const configurationData = createMemo(() => {
    return queries.configurationQuery.data ?? null;
  });

  // Get access policy data
  const accessPolicyData = createMemo(() => {
    return queries.accessPolicyQuery.data ?? null;
  });

  // Get combined assignees data (users + groups) transformed to ITagItem format
  const allAssigneesData = createMemo((): ITagItem[] => {
    const users = projectUsersData();
    const groups = projectGroupsData();

    if (!users || !groups) {
      return [];
    }

    const tags: ITagItem[] = [];

    // Add groups first
    (groups as ProjectGroup[]).forEach((group) => {
      tags.push({
        value: `${group.id}||GROUP||${group.id}`,
        label: group.name,
        isValid: true,
        id: group.id,
      });
    });

    // Add users second (filter out REMOVED users)
    (users as Array<ProjectUser | null | undefined>).forEach((user) => {
      if (!user || user.status === 'REMOVED') {
        return;
      }
      tags.push({
        value: `${user.id}||USER||${user.tiduuid || user.id}`,
        label: `${user.firstName} ${user.lastName} (${user.email})`,
        isValid: true,
        id: user.id,
        tiduuid: user.tiduuid,
      });
    });

    return tags;
  });

  // Get follow status data for all issues
  const followStatusData = createMemo(() => {
    const followQuery = queries.followStatusQuery();
    if (!followQuery?.data) {
      return null;
    }
    return followQuery.data;
  });

  return {
    issuesData,
    projectTagsData,
    projectUsersData,
    projectGroupsData,
    configurationData,
    accessPolicyData,
    allAssigneesData,
    followStatusData,
  };
}
